import {
  ReviewCountBadge,
  H3,
  Paragraph,
  SynonymAccordionTitle,
  SynonymAccordionText,
  SynonymAccordionHeader,
  AccordionLeadSlot,
  ACCORDION_BODY_INDENT,
  SynonymTermAccordionTitle,
  TermChips,
  TermChip,
} from './styled';
import { useDispatch, useSelector } from 'react-redux';
import { getNLPModalState } from 'selectors/searchNLP';
import {
  toggleSynonymsEnabled,
  toggleTermEnabled,
  toggleSynonym,
} from 'actions/searchNLP';
import Accordion from 'components/common/Accordion';
import { Switch, Tooltip } from 'antd';
import React, { useMemo } from 'react';
import { selectedDistinctLowers } from 'reducers/searchNLP/countDistinctSelections';
import { isExcludedTerm } from 'reducers/searchNLP/termSides';
import { chipStateTooltip } from './chipTooltip';

/**
 * True when at least one available suggestion is not in the selected set — i.e.
 * the panel hides an off-by-default term the user might otherwise miss.
 *
 * @param {string[]} available
 * @param {string[]} selected
 * @returns {boolean}
 */
const hasUnselectedSuggestion = (available, selected) =>
  available.some(item => !selected.includes(item));

/**
 * Renders synonym review UI for NLP stage 2.
 * All state lives in Redux under nlpModal.
 */
export function NLPSynonymAccordion({ conflictLabels }) {
  const dispatch = useDispatch();
  const {
    aiSearchInterpretation,
    synonymsEnabled,
    termEnabled,
    selectedSynonyms,
    termSideMap,
  } = useSelector(getNLPModalState);

  const termSynonyms = aiSearchInterpretation?.termSynonyms;

  // Distinct count: collapse duplicates that the chip-link creates when the same
  // synonym is shown under multiple OR-sibling sub-panels, and exclude selections
  // under terms whose row Switch is off (they don't go into the final query).
  const totalSelectedSynonyms = useMemo(
    () => selectedDistinctLowers(selectedSynonyms, termEnabled).size,
    [selectedSynonyms, termEnabled]
  );

  const totalAvailableSynonyms = useMemo(
    () =>
      termSynonyms
        ? Object.values(termSynonyms).reduce((s, syns) => s + syns.length, 0)
        : 0,
    [termSynonyms]
  );
  const synonymsTitleWord =
    totalAvailableSynonyms === 1 ? 'related term' : 'related terms';

  if (!termSynonyms) return null;
  const termEntries = Object.entries(termSynonyms);
  if (termEntries.length <= 0) return null;
  const hasSingleTerm = termEntries.length === 1;

  return (
    <Accordion
      // Always open — with opt-in defaults the user must be able to see and pick
      // suggestions without an extra click, whatever is currently selected.
      isCollapsedByDefault={false}
      bodyIndent={ACCORDION_BODY_INDENT}
      title={
        <SynonymAccordionTitle>
          <AccordionLeadSlot>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <span onClick={e => e.stopPropagation()}>
              <Switch
                data-testid='nlp-synonyms-master-switch'
                checked={synonymsEnabled}
                onChange={() => dispatch(toggleSynonymsEnabled())}
                size='small'
              />
            </span>
          </AccordionLeadSlot>
          <SynonymAccordionText>
            <SynonymAccordionHeader>
              <H3>Include {synonymsTitleWord}</H3>
              <ReviewCountBadge>{totalSelectedSynonyms}</ReviewCountBadge>
            </SynonymAccordionHeader>
            <Paragraph>Select related terms created from your search</Paragraph>
          </SynonymAccordionText>
        </SynonymAccordionTitle>
      }
      testId='nlp-search-review-synonyms'
    >
      {termEntries.map(([term, synonyms]) => (
        <NLPTermContent
          key={term}
          term={term}
          synonyms={synonyms}
          selectedSynonyms={selectedSynonyms[term] || []}
          termEnabled={!!termEnabled[term]}
          excluded={isExcludedTerm(termSideMap, term)}
          conflictLabels={conflictLabels}
          isSingleTerm={hasSingleTerm}
          onToggleTerm={() => dispatch(toggleTermEnabled(term))}
          onToggleSynonym={synonym => dispatch(toggleSynonym(term, synonym))}
        />
      ))}
    </Accordion>
  );
}

/**
 * @param props
 * @param {string} props.term
 * @param {string[]} props.synonyms - all available synonyms for this term
 * @param {string[]} props.selectedSynonyms - currently selected synonyms
 * @param {boolean} props.termEnabled - per-term toggle
 * @param {boolean} props.excluded - term sits on the exclusion side (red chips)
 * @param {Set<string>} [props.conflictLabels] - lowercased labels selected on both sides (amber)
 * @param {() => void} props.onToggleTerm
 * @param {(synonym: string) => void} props.onToggleSynonym
 */
function NLPTermContent(props) {
  const isConflicted = (synonym, isSelected) =>
    isSelected && !!props.conflictLabels?.has(synonym.toLowerCase());

  if (props.isSingleTerm) {
    return (
      <>
        <SynonymTermAccordionTitle>
          {props.term}
          <ReviewCountBadge>{props.selectedSynonyms.length}</ReviewCountBadge>
        </SynonymTermAccordionTitle>
        <TermChips>
          {props.synonyms.map(synonym => {
            const isSelected = props.selectedSynonyms.includes(synonym);
            const conflicted = isConflicted(synonym, isSelected);
            const tooltip = chipStateTooltip({
              label: synonym,
              excluded: props.excluded,
              selected: isSelected,
              conflicted,
            });
            const chip = (
              <TermChip
                data-testid={`nlp-synonym-chip-${props.term}-${synonym}`}
                selected={isSelected}
                excluded={props.excluded}
                conflict={conflicted}
                aria-pressed={isSelected}
                onClick={() => props.onToggleSynonym(synonym)}
              >
                {synonym}
                {isSelected && <span aria-hidden='true'>×</span>}
              </TermChip>
            );
            return tooltip ? (
              <Tooltip key={synonym} title={tooltip}>
                {chip}
              </Tooltip>
            ) : (
              <React.Fragment key={synonym}>{chip}</React.Fragment>
            );
          })}
        </TermChips>
      </>
    );
  }

  return (
    <Accordion
      isCollapsedByDefault={
        !hasUnselectedSuggestion(props.synonyms, props.selectedSynonyms)
      }
      title={
        <SynonymTermAccordionTitle>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <span onClick={e => e.stopPropagation()}>
            <Switch
              data-testid={`nlp-synonym-switch-${props.term}`}
              checked={props.termEnabled}
              onChange={props.onToggleTerm}
              size='small'
            />
          </span>
          {props.term}
          <ReviewCountBadge>{props.selectedSynonyms.length}</ReviewCountBadge>
        </SynonymTermAccordionTitle>
      }
      testId={`nlp-synonym-term-${props.term}`}
    >
      <TermChips>
        {props.synonyms.map(synonym => {
          const isSelected = props.selectedSynonyms.includes(synonym);
          const conflicted = isConflicted(synonym, isSelected);
          const tooltip = chipStateTooltip({
            label: synonym,
            excluded: props.excluded,
            selected: isSelected,
            conflicted,
          });
          const chip = (
            <TermChip
              data-testid={`nlp-synonym-chip-${props.term}-${synonym}`}
              selected={isSelected}
              excluded={props.excluded}
              conflict={conflicted}
              aria-pressed={isSelected}
              onClick={() => props.onToggleSynonym(synonym)}
            >
              {synonym}
              {isSelected && <span aria-hidden='true'>×</span>}
            </TermChip>
          );
          return tooltip ? (
            <Tooltip key={synonym} title={tooltip}>
              {chip}
            </Tooltip>
          ) : (
            <React.Fragment key={synonym}>{chip}</React.Fragment>
          );
        })}
      </TermChips>
    </Accordion>
  );
}
